/**
 * Response shapes for the routes the editor reads on every deck open.
 *
 * These two payloads were written NOWHERE before this module — they existed
 * only as inline `c.json({…})` object literals in `routes/slides.ts`, so
 * neither side of the wire declared them. Adding a field was invisible to the
 * client; renaming one was invisible to both, and surfaced as `undefined` in a
 * preview iframe. Naming them makes the route's return an assertion the type
 * checker enforces (`c.json<DeckSummaryPayload>(…)`) and gives the client's
 * generic `api.get<T>` something real to be parameterised with.
 *
 * Scope is deliberately narrow: the two payloads the plan named, not a
 * speculative map of all ~90 routes. Add a shape here when a client file that
 * consumes it is converted, so every entry has a reader that proves it.
 *
 * See `./deck.ts` for the domain types these compose, and for why this
 * directory imports nothing.
 */

import type {
  BankCategory,
  BankItem,
  DeckOptions,
  DeckSection,
  DeckSummary,
  EditableField,
  LayoutFinding,
  ListGroup,
  MediaField,
  PlannedSlide,
  PlanSection,
  SlideAccent,
  SlideEntry,
  SlideLayout,
  SlideOption,
} from './deck.ts';
import type { CategoryEntry, Person, SavedView, TagEntry } from './organisation.ts';
import type { Swatch } from './palette.ts';

// The organisation model lives in organisation.ts / palette.ts; these names are
// what the dashboard and Library clients (built 2026-09 against the older
// `CategoryColour` vocabulary) import, so they are re-exported here rather than
// rewritten at forty call sites. A category colour IS a swatch.
export type { CategoryEntry, Person, SavedView, TagEntry } from './organisation.ts';
export type CategoryColour = Swatch;
/** The swatches a category may be tinted with, in picker order. The same list
 *  as `swatches()` — spelt out rather than imported because this directory
 *  allows only type-only imports (`__tests__/purity.test.ts`); the pairing is
 *  pinned by `__tests__/category-colours.test.ts`. */
export function categoryColours(): CategoryColour[] {
  return ['orange', 'pink', 'violet', 'cyan', 'blue', 'lime', 'yellow', 'slate', 'ink'];
}

/** `GET /api/slides/:slug/_summary` — everything the editor needs to draw the
 *  filmstrip and the deck chrome, in one request. */
export type DeckSummaryPayload = {
  slug: string;
  /** The deck's DISPLAY name (the dashboard's rule), so editor surfaces that
   *  name the deck don't have to fall back to the slug. */
  title: string;
  sections: DeckSection[];
  slides: SlideEntry[];
  /** Deck-level display switches. The Edit tab needs them to know whether a
   *  per-slide override is meaningful — offering "Section label" on a deck
   *  that shows none would be a switch with nothing behind it. */
  options: DeckOptions;
};

/** `GET /api/slides/:slug/file?id=…` — one slide's source plus every
 *  deterministic-codec reading of it. This IS the Edit tab. */
export type SlideFilePayload = {
  id: string;
  title: string;
  section: string;
  /** Absolute path on the server. */
  file: string;
  source: string;
  /** Hash of `source` at read time — send it back on a write and the route
   *  409s `stale` if the file moved on meanwhile. */
  sourceHash: string;
  /** Children text and text props, flat. `lists` says which belong together. */
  fields: EditableField[];
  lists: ListGroup[];
  /** null for a bespoke slide: no template wrapper, so nothing to switch. */
  layout: SlideLayout | null;
  accent: SlideAccent;
  media: MediaField[];
  options: SlideOption[];
};

/**
 * One settled change in a deck's Activity feed — every mutating tool card
 * across all its chat threads, joined against the edit history.
 *
 * Lived in `routes/chat.ts` until the client's own reader of it
 * (`client/lib/activityStacks.ts`) was typed. That file imports `node:fs`, so
 * the browser could never import this declaration and the Activity panel had to
 * assume every field name.
 */
export type ActivityRow = {
  toolId: string;
  threadKey: string;
  scopeKind: ChatScopeKind;
  tool: string;
  slideId: string | null;
  ts: number;
  status: ActivityStatus;
  label: string;
  previewing: boolean;
  historyId?: string;
  revertible?: boolean;
  reapplyable?: boolean;
  /** A change an interrupted run left on the slide that containment could not
   *  attribute and so deliberately did not undo. Recorded in the history purely
   *  so it stays revertible; the UI labels it differently from a change the
   *  user chose to apply. */
  leftover?: boolean;
  /** A create_slide proposal that is already IN the deck awaiting its decision
   *  (llm/pending-slides.ts) — the filmstrip marks it New, and `slideId` is the
   *  real one, so the review walk can jump to it like any other proposal. */
  pending?: boolean;
  /** Included only while the card is still actionable (Apply from the tab).
   *  `unknown` rather than `any`: it is a tool's own argument object, so a
   *  reader has to check what it pulls out of it. */
  input?: Record<string, unknown>;
};

/** Which half of the chat contract a row or run belongs to. */
export type ChatScopeKind = 'slide' | 'deck';

export type ActivityStatus = 'ready' | 'applied' | 'rejected' | 'reverted' | 'error';

/** A turn the SERVER currently has in flight. Sent alongside the settled rows
 *  so a refresh, a second tab or a colleague still sees a running edit and can
 *  stop it. `stop` is the server's own handle and never crosses the wire. */
export type ActiveRunRow = {
  slug: string;
  /** '_deck' or the slide's base id — the stop key's second half. */
  threadKey: string;
  scopeKind: ChatScopeKind;
  /** Base slide ids this run is editing; empty for open-ended deck scope. */
  slideIds: string[];
  model: string;
  startedAt: number;
  /** The hard limit this run is running under, so the UI can show "3m of 8m". */
  hardMs: number;
  /** Selection runs only — slides written so far. */
  progress: { done: number; total: number } | null;
};

/** `GET /api/chat/:slug/history`. */
export type DeckActivityPayload = {
  rows: ActivityRow[];
  running: ActiveRunRow[];
};

/** `POST /api/assets/:slug` — a multipart upload's per-file outcome. Partial
 *  success is the norm rather than an edge case: one oversized file in a
 *  five-file drop still writes the other four, so both arrays are always
 *  present and either may be empty. */
export type AssetUploadPayload = {
  /** Filenames as WRITTEN, which is not always what was sent — a name
   *  collision whose bytes differ gets a `-1`/`-2` suffix. */
  uploaded: string[];
  errors: { name: string; error: string }[];
};

/**
 * What every deterministic-codec write answers with: the slide's refreshed
 * payload, plus a count of what it touched.
 *
 * Partial because a narrow write refreshes only what it could have changed —
 * setting an option returns `options` and the new hash, not the whole slide.
 * The Edit tab's state layer merges each response over what it holds
 * (`res.fields || s.fields`), which is only sound BECAUSE these are optional.
 */
export type SlideWritePayload = Partial<SlideFilePayload> & {
  /** Required, unlike the rest: the rolling hash IS the write contract. The
   *  client sends the last one back as `ifMatch`, so a response without one
   *  would silently break the next save rather than fail here. */
  sourceHash: string;
  /** slide-accent-codec: how many colour references were swapped. */
  replaced?: number;
  /** slide-layout-codec: how many layout attrs the variant switch wrote. */
  changed?: number;
};

/**
 * `POST /api/slides/:slug/slide/from-upload` — finished artwork becoming a
 * slide. NOT a `SlideWritePayload`: this route creates rather than edits, so
 * it answers with what it made (including the asset it wrote alongside) and
 * carries no `sourceHash`.
 */
export type SlideFromUploadPayload = {
  ok: true;
  slideId: string;
  localName: string;
  title: string;
  assetName: string;
};

/** `GET /api/chat/:slug/_slide/:slide/preview-status` — server truth for a
 *  re-mounted card. The wire says original/proposed; the pills say
 *  current/candidate (`showingFromWire` in client/lib/reviewFlip.ts). */
export type PreviewStatusPayload = {
  previewing: boolean;
  showing: 'proposed' | 'original' | null;
};

/**
 * `GET /api/settings`, as far as the MODEL PICKERS are concerned.
 *
 * Deliberately partial: the ⚙ drawer reads the rest of this payload
 * (providers, media availability, storage) and widens this when it converts.
 * Every field is optional because the client treats a missing engine as
 * unavailable rather than as an error.
 */
export type SettingsPayload = {
  /** The stored, editable layer — what the ⚙ drawer round-trips. */
  settings?: LLMSettings;
  status?: LLMStatus;
  /** Which providers have a server-side key. Never the keys themselves. */
  available?: Record<string, boolean>;
  media?: { unsplash?: boolean; pexels?: boolean; openverse?: boolean };
  claude?: {
    available?: boolean;
    authPresent?: boolean;
    /** Absolute path to the resolved binary, when one was found. */
    bin?: string;
    /** Why it wasn't, when it wasn't. */
    reason?: string;
    /** The ⚙ "Default model", already resolved server-side. */
    model?: string;
    /** The ⚙ "Offer Claude Sonnet" toggle, already applied — the client must
     *  not re-derive this rule. */
    offerSonnet?: boolean;
    /** The effort actually in force for chat turns (setting → env → default),
     *  so the drawer shows the effective value rather than the fallback chain. */
    chatEffort?: string;
    chatEffortLevels?: readonly string[];
  };
  fastChat?: {
    available?: boolean;
    provider?: string | null;
    model?: string | null;
  };
  /** The ChatGPT-plan agent, present only when the server switch is on and the
  /** The Pi chat engine, present only when the server switch is on and the
   *  engine is configured. Same shape as `fastChat` so the pill treats them
   *  alike — and ALWAYS metered, which is why the pill needs no auth field
   *  here: Pi has one legal mode. */
  piChat?: {
    available?: boolean;
    issue?: string | null;
    model?: string | null;
    effort?: string | null;
  };
  /** The gateway address actually in force (setting → env), so the drawer shows
   *  the effective value rather than re-deriving the fallback. */
  gatewayBaseUrl?: string | null;
  /** Which engine a CHAT turn starts on, and per-engine reasoning ladders.
   *  `engine` is already reduced to something startable — never re-derive it. */
  chat?: EngineChoiceBlock;
  /** The same, for whole-deck BUILDS: the ⚙ default reduced to an engine that
   *  can actually start. */
  build?: EngineChoiceBlock;
  /** Masked credential status only — present, a last-four hint, and whether the
   *  value comes from the drawer or the server env. No route returns a key. */
  credentials?: Record<string, CredentialStatus>;
  /** Model id → the reasoning rungs THAT model takes, for the dials whose model
   *  is free text (Pi's). Only ids the server recognises get a key, and the
   *  absence of one means "never heard of this model" — offer the full ladder
   *  and let the endpoint judge — never "this model has no rungs". An EMPTY
   *  array is the third state: it reasons, with no settable rung. */
  modelRungs?: Record<string, string[] | undefined>;
};

/** One provider Pi can be signed in to, as PI declares it (`GET
 *  /api/settings/pi/providers`). The list is the CLI's, never a constant in
 *  this studio: that is what lets a Pi release add a subscription without one
 *  of ours. */
export type PiOAuthProvider = {
  id: string;
  /** Pi's own display name, e.g. "OpenAI (ChatGPT Plus/Pro)". */
  name: string;
  /** A plan rather than pay-as-you-go. False for OpenRouter and Radius, whose
   *  logins draw on credits — so the drawer can avoid calling either free. */
  isSubscription: boolean;
  loginLabel?: string;
  loggedIn: boolean;
};

/** One model the signed-in Pi provider serves (`GET /api/settings/pi/models`).
 *  The catalogue is the CLI's, so the drawer can offer a real list on the
 *  subscription branch where the field had to be free text — and free text
 *  stays underneath, for an id shipped after this list was fetched.
 *
 *  `vision` is THREE-STATE: null means the catalogue did not say, and that must
 *  not read as "cannot see" — the fresh-eyes review hands the model pictures,
 *  and a wrong "no" there silently downgrades it to reading source. */
export type PiCatalogueModel = {
  id: string;
  name: string;
  vision: boolean | null;
  contextWindow?: number;
};

/** `POST /api/settings/pi/models/refresh` — the provider's catalogue re-read.
 *  `issue` is the degraded answer, not a failure: the list is whatever the
 *  server could still offer, and the string says what to do about it (including
 *  the `pi update --models` a shell on the server can always run). */
export type PiModelRefresh = {
  provider: string;
  models: PiCatalogueModel[];
  /** ISO timestamp of the attempt, whether or not it changed anything. */
  refreshedAt: string;
  issue?: string;
};

/** A sign-in in progress, polled by the drawer. Every event in it is Pi's own
 *  (`device_code` is the only shape that can complete on a REMOTE server — a
 *  browser flow's callback lands on the server's localhost, not the viewer's). */
export type PiLoginState = {
  id: string;
  provider: string;
  status: 'running' | 'done' | 'error' | 'cancelled';
  events: PiLoginEvent[];
  /** Set when Pi is blocked on the user: a code to paste, or a choice of login
   *  method. */
  prompt?: PiLoginPrompt | null;
  error?: string | null;
};

export type PiLoginEvent =
  | { type: 'info'; message: string; links?: { url: string; label?: string }[] }
  | { type: 'auth_url'; url: string; instructions?: string }
  | { type: 'device_code'; userCode: string; verificationUri: string; expiresInSeconds?: number }
  | { type: 'progress'; message: string };

export type PiLoginPrompt = {
  kind: 'text' | 'secret' | 'select' | 'manual_code';
  message: string;
  placeholder?: string;
  options?: { id: string; label: string; description?: string }[];
};

/** One engine as the settings payload reports it. `auth` says WHOSE credential
 *  it would spend, which is the only field here that decides whether work costs
 *  money. */
export type EngineInfo = {
  available?: boolean;
  reason?: string;
  issue?: string;
  model?: string | null;
  auth?: string | null;
  /** The rung in force with no override, and the rungs this MODEL actually
   *  takes — server-derived (llm/chat-efforts.ts), never a list the client
   *  keeps. An engine with no `efforts` gets no dial at all. */
  effort?: string | null;
  efforts?: string[];
  /** Pi only, and the reason these are here rather than derived client-side:
   *  on that engine the PROVIDER is a setting, so the row offering it has to
   *  say what the next build would actually talk to. Absent on every other
   *  engine, which is welded to one endpoint each. */
  baseUrl?: string | null;
  api?: string | null;
  contextWindow?: number | null;
  /** Pi only: which OAuth provider a subscription run uses, as Pi's CLI names
   *  it. Reported in BOTH modes — in 'api' mode it is what a switch to the
   *  subscription would run, which is exactly what the drawer's row has to be
   *  able to say. `baseUrl`/`api`/`contextWindow` come back null on the
   *  subscription branch: they are the built-in provider's business, not ours. */
  subscriptionProvider?: string | null;
  /** Pi builds only: whether the fresh-eyes render review runs (the
   *  `piReviewPass` toggle, default on). */
  reviewPass?: boolean;
  /** Pi builds only: whether the server looks at every render with a vision
   *  model when the reviewing model cannot (the `piRenderDescribe` toggle,
   *  default on). */
  renderDescribe?: boolean;
  /** Pi builds only: whether the "don't draw illustrations in SVG" rule is
   *  lifted for the build (the `piSvgArt` toggle, default off). */
  svgArt?: boolean;
  /** Pi builds only: whether step 5 fans out to one child writer per content
   *  section (the `piSubagents` toggle, default on). */
  subagents?: boolean;
  /** Pi builds only: the saved provider setups a build may be pointed at, so
   *  the new-deck wizard can offer one PER BUILD instead of the author having
   *  to change the ⚙ drawer between two arms of a comparison.
   *
   *  Sent on the engine block rather than read from the raw settings blob so
   *  the wizard and the drawer read the SAME source — the two rows can then
   *  never disagree about which setups exist. Labels only travel back: the
   *  build POST names a profile, and the server resolves it against this same
   *  stored list. */
  profiles?: PiProfile[];
  /** Pi builds only: the model that review would run on, or null when it
   *  follows the build model. Reported because blank is a real answer, and the
   *  drawer has to say which model would look at the renders. */
  reviewModel?: string | null;
};

/** `engine` is the preference already reduced to something startable;
 *  `preference` is what was actually asked for, kept so the drawer can say when
 *  the two differ. */
export type EngineChoiceBlock = {
  engine?: string;
  preference?: string;
  engines?: Record<string, EngineInfo | undefined>;
};

/** What `credentialStatuses()` reports per key. The VALUE never crosses the
 *  wire — `hint` is a masked tail. */
export type CredentialStatus = {
  present?: boolean;
  source?: 'stored' | 'env';
  hint?: string;
  /** Removing a STORED key would fall back to one in the server environment. */
  envFallback?: boolean;
};

/**
 * The runtime settings store — `.studio-state/settings.json`, read by the
 * server on every request and edited by the ⚙ drawer.
 *
 * Declared here rather than beside its reader/writer (`server/llm/settings.ts`,
 * which imports `node:fs`) because the drawer both READS and WRITES it over the
 * wire; that module re-exports this declaration, so no server importer moved.
 *
 * Env vars stay the home of SECRETS (API keys, gateway base URL); this store
 * holds only the non-secret choices a maintainer flips without a redeploy.
 */
/**
 * One saved Pi provider setup. See `LLMSettings.piProfiles`.
 *
 * `label` is the only field a human writes; everything else mirrors a live
 * setting exactly, so applying a profile is a copy and never a translation.
 */
export type PiProfile = {
  /** What the drawer calls it. Free text, the one field that is not a mirror. */
  label: string;
  /** Which branch this setup uses — the same two values as `piAuth`. */
  auth: 'subscription' | 'api';
  /** Subscription branch: Pi's own provider id. Ignored on the api branch. */
  subscriptionProvider?: string | null;
  /** API branch: the OpenAI-compatible endpoint. Ignored on the subscription
   *  branch, where the login owns the address. */
  baseUrl?: string | null;
  /** API branch: the wire format. Carried per profile because it is a fact
   *  about the PROVIDER and getting it wrong is fatal rather than degraded —
   *  measured 2026-08-10, `gpt-5.6-luna` returns `stopReason: "error"` over
   *  chat completions and answers over Responses, while `deepseek-v4-flash` is
   *  happy on completions. One global field could not hold both answers. */
  api?: 'openai-completions' | 'openai-responses' | null;
  /** The model id, as that endpoint names it. */
  model?: string | null;
};

export type LLMSettings = {
  /** Active provider override. null/absent → env LLM_PROVIDER / auto-detect. */
  provider?: 'gateway' | 'openai' | null;
  /** Gateway model override — the Fast chat model. Gateway preset only. */
  model?: string | null;
  /** Vision-capable model — chat turns with image attachments route here
   *  so the model actually sees the picture. Gateway preset only. */
  visionModel?: string | null;
  /**
   * FALLBACK vision model for reading the pictures in a brief at intake,
   * transcribing them into the brief markdown so the build gets the content as
   * text. Gateway preset only; blank → BRIEF_IMAGE_MODEL_DEFAULT.
   *
   * A FALLBACK since 2026-08-19, not the reader. Every picture the server read
   * used to go through this model whichever engine was building, which tied the
   * studio's eyes to the OpenCode gateway credential — the one that is ending.
   * The engine about to build reads its own pictures now, through its own CLI
   * (pipeline/vision-reader.ts); this slot answers only when that model is one
   * the roster knows to be text-only, or when no engine is configured at all.
   *
   * A SEPARATE dial from `visionModel` on purpose, the same way the Fast BUILD
   * model is separate from the Fast CHAT model (2026-08-03): these are
   * different jobs. `visionModel` carries a conversation about a picture;
   * this one does a single cheap transcription, once, per brief. Measured
   * 2026-08-04 on the content-team flow charts, both models read them
   * correctly — but `mimo-v2.5` costs ~26x less per request on the Go quota
   * than `kimi-k2.6`, which is the wrong reason to force one choice on both
   * surfaces.
   *
   * Why it exists at all: a brief's content can BE a picture. The content-team
   * docx carried its whole delivery process only as flow charts, and two
   * DeepSeek builds shipped without that section and reported success, because
   * a text-only model receives an image as nothing.
   */
  briefImageModel?: string | null;
  /** Reasoning effort for the `gateway` preset — sent as `reasoning_effort` on
   *  the chat/completions body. null/absent → the parameter is NOT sent at all
   *  and the gateway's own per-model default stands: it spans many models with
   *  different curves, so it is opt-in rather than defaulted. */
  gatewayEffort?: string | null;
  /** The gateway model a Fast BUILD runs on, when it should NOT be the chat
   *  slot above. null/absent → the build follows `model` (then the
   *  `LLM_GATEWAY_MODEL` env), which is what it always did.
   *
   *  Its own dial because the two jobs are not the same job: chat rewrites one
   *  slide and is judged on how fast it answers, a build writes a whole deck
   *  over many minutes and is judged on the deck. Wanting a bigger model for
   *  the build than for the chat pill is the ordinary case, and before this the
   *  only way to have it was to change the chat model and remember to change it
   *  back. */
  fastBuildModel?: string | null;
  /** Reasoning effort for a Fast BUILD — written into the per-build opencode
   *  overlay as the picked model's `reasoningEffort` option, which the CLI
   *  passes through to the gateway as `reasoning_effort` (verified 2026-08-03
   *  by sending a bogus rung and reading the gateway's complaint back).
   *
   *  Kept apart from `gatewayEffort` for the same reason as the model: that dial
   *  is tuned for a chat turn's latency, and `none` — the rung worth reaching
   *  for there — is the last thing you want writing 20 slides. null/absent →
   *  env FAST_BUILD_EFFORT, then nothing is sent at all. */
  fastBuildEffort?: string | null;
  /** Whether this server OFFERS Claude at all. false takes it off BOTH surfaces
   *  — no agent builds, and the chat pill does not list it — even when the
   *  binary and the login are both fine. true/null/absent → offered (every
   *  consumer gates on `=== false`, so absence is never a decision).
   *
   *  The switch an instance other people use leans on: their turns must not
   *  reach this server's subscription. */
  claudeEnabled?: boolean | null;
  /** The same switch for the OpenCode engine, and for the same reason in
   *  reverse: this one is always metered, so taking it off is how a server
   *  stops being able to spend the gateway key at all. Builds and chat
   *  together — a switch that left one surface running would be the kind of
   *  half-answer that costs money quietly. */
  fastEnabled?: boolean | null;
  /** The same switch for Pi. Separate from `STUDIO_PI_BUILD=1`: the env flag is
   *  the deploy's decision that this engine may exist here at all, this one is
   *  the maintainer's decision to offer it today. */
  piEnabled?: boolean | null;
  /** Absolute path to the claude binary (overrides PATH lookup). */
  claudeBin?: string | null;
  /** Which engine headless deck builds run on by DEFAULT — the value the
   *  new-deck wizard's Build engine row starts on. The wizard can override it
   *  for one build (an A/B needs two runs back to back, not a trip to the
   *  drawer between them), so this is a preference, not a lock. Unknown or
   *  unavailable values fall back to 'claude': an engine that is off on this
   *  server must never be able to make the wizard unusable. */
  buildEngine?: 'claude' | 'fast' | 'pi' | null;
  /** Default model for Claude CHAT and section builds: 'opus' (default) or
   *  'sonnet'. Whole-deck builds read `claudeBuildModel` instead. */
  claudeModel?: string | null;
  /** The model a whole-deck Claude BUILD runs on: 'opus' (default) or 'sonnet'.
   *  Its own dial so every engine's build model is answered in one place — the
   *  ⚙ drawer's Deck builds section — rather than Claude's being a client
   *  constant you could only change by editing code. Independent of
   *  `offerSonnet`, which governs the chat pill: choosing a build model is a
   *  maintainer's decision about deck quality, not a per-message choice. */
  claudeBuildModel?: string | null;
  /** The OpenAI-compatible endpoint behind the Fast model, e.g.
   *  `https://opencode.ai/zen/go/v1`.
   *
   *  A SETTING rather than a credential, because a URL is not a secret — and it
   *  has to be settable here at all because its KEY became settable in the
   *  drawer (2026-08-03). Until then the two were both env, so they moved
   *  together; afterwards, pasting a gateway key on a fresh server left a
   *  half-configured provider and a message telling the user to edit a file
   *  they cannot reach.
   *
   *  Resolves settings → `LLM_GATEWAY_BASE_URL` env, so an existing deploy is
   *  unchanged and a drawer value simply overrides it. */
  gatewayBaseUrl?: string | null;
  /** WHOSE CREDENTIAL A CLAUDE RUN SPENDS — builds, chat, section builds and
   *  the planner alike, because they are ONE engine and a gate that passes on
   *  one credential over a turn that spends another is a lie in the one place
   *  it costs money to believe.
   *
   *  'subscription' (the default, and what absent/unknown resolves to) runs
   *  `claude -p` on the Claude Code login this server holds:
   *  CLAUDE_CODE_OAUTH_TOKEN from `claude setup-token`, or an interactive
   *  `/login` under CLAUDE_CONFIG_DIR. Flat-rate at the point of use. 'api' runs
   *  the same CLI against a METERED Anthropic API key.
   *
   *  THE STUDIO READS `STUDIO_ANTHROPIC_API_KEY` AND WRITES `ANTHROPIC_API_KEY`,
   *  and those are deliberately not one variable. The CLI reads the latter, and
   *  measured on Claude Code 2.1.220 it accepts an ambient one under `-p` with
   *  NO approval prompt and demotes the subscription's Authorization header to
   *  x-api-key — so inheriting it moved every build onto Console billing with
   *  nothing on screen saying so. Nothing inherits it any more
   *  (CLAUDE_BILLING_VARS in agent/build-runner-shared.ts); the key travels only
   *  on this setting's 'api' branch, which makes turning it on a decision taken
   *  twice: once where the key is set, once in the drawer.
   *
   *  The KEY IS NEVER STORED HERE. It lives in the write-only credential store
   *  (llm/credentials.ts) or the server env, and the settings API reports
   *  `apiKeyPresent`, never a value. The failure direction is deliberate in
   *  every reader: anything that is not exactly 'api' means the subscription, so
   *  a corrupt settings.json, a stale client or a typo keeps working for free
   *  rather than silently starting to bill. See `getClaudeAuth()` and
   *  `resolveClaudeAuth()` in agent/build-runner-shared.ts. */
  claudeAuth?: 'subscription' | 'api' | null;
  /** WHOSE CREDENTIAL A PI RUN SPENDS: 'api' (the generated `studio-pi`
   *  provider on `STUDIO_PI_API_KEY`, metered) or 'subscription' (a `pi /login`
   *  sitting in the studio's own Pi home — ChatGPT Plus/Pro, Claude Pro/Max,
   *  Copilot, xAI, OpenRouter, Radius; `piSubscriptionProvider` picks which).
   *
   *  null/absent → env `PI_AUTH`, then 'api'. It is the one engine whose
   *  default is the metered branch, because Pi does not run at all until
   *  someone sets `STUDIO_PI_BUILD=1` and pastes a key — see `getPiAuth`. */
  piAuth?: 'subscription' | 'api' | null;
  /** WHICH OAuth provider a subscription run uses, as Pi's own CLI names it
   *  (`openai-codex`, `anthropic`, `github-copilot`, `xai`, `openrouter`,
   *  `radius`, …). Free text, validated as a slug: Pi's roster grows on its own
   *  release cycle and an allowlist here would need a studio release to follow
   *  it. null/absent → env `PI_SUBSCRIPTION_PROVIDER`, then 'openai-codex' — a
   *  default, not a special case. Ignored entirely in 'api' mode. */
  piSubscriptionProvider?: string | null;
  /**
   * SAVED PROVIDER SETUPS, so comparing providers is a click rather than
   * retyping four fields and remembering the wire format.
   *
   * Pure SNAPSHOTS of the live `pi*` fields, never a second source of truth:
   * applying one copies its values into `piAuth` / `piSubscriptionProvider` /
   * `piBaseUrl` / `piApi` / `piModel`, which is what every resolver already
   * reads. Nothing on the build path learns about profiles at all, so a
   * malformed list can misconfigure a build no more than the fields themselves
   * can — and there is no "active profile" pointer to go stale, because which
   * one is active is DERIVED by comparing the live fields to each entry.
   *
   * A profile deliberately holds NO API KEY. `MANAGED_CREDENTIALS` is a closed,
   * typed map and per-profile keys would mean dynamic credential names in the
   * one file that must stay easy to audit. In practice it costs little: Pi's own
   * OAuth roster covers OpenRouter, Anthropic, Copilot, Kimi and Radius as
   * LOGINS, so most provider variety needs no key, and the metered branch keeps
   * the single `STUDIO_PI_API_KEY`. Two metered providers with different keys
   * still means pasting a key when you switch.
   */
  piProfiles?: PiProfile[] | null;
  /** THE PI ENGINE'S OWN ENDPOINT — never the Fast gateway's.
   *
   *  Pi (`@earendil-works/pi-coding-agent`) reaches any OpenAI-compatible
   *  provider through a generated `models.json`, and this is the base URL that
   *  goes into it. It is deliberately NOT `gatewayBaseUrl`: that one feeds Fast
   *  chat AND Fast build, so trialling a provider on Pi by repointing it broke
   *  chat for the duration of the trial (2026-08-07). One engine, one endpoint.
   *
   *  Resolves settings → env `PI_BASE_URL` → '' (not configured). A URL, not a
   *  secret, so it lives here; the key is in the credential store under
   *  `STUDIO_PI_API_KEY`. */
  piBaseUrl?: string | null;
  /** WHICH WIRE FORMAT Pi speaks to that endpoint: 'openai-completions'
   *  (chat/completions, the common case) or 'openai-responses' (the Responses
   *  API, which OpenAI and Meta recommend and which the Fast engine's shim
   *  cannot carry at all). An ALLOWLIST rather than free text — it selects
   *  executed wire code inside Pi, so a typo is a build that dies at the first
   *  request. null/absent → 'openai-completions'. */
  piApi?: 'openai-completions' | 'openai-responses' | null;
  /** The model id a Pi build runs on, exactly as the endpoint names it (e.g.
   *  'deepseek-v4-flash'). Free text on purpose: the whole point of this engine
   *  is that a new provider is configuration, and no roster we ship could keep
   *  up. null/absent → env `PI_MODEL`, then nothing — and the gate refuses the
   *  build rather than guessing an id. */
  piModel?: string | null;
  /** Does a Pi build run the FRESH-EYES render review — a fresh session
   *  looking at server-rendered pictures of every slide — before the repair
   *  pass? absent/null/true → yes (the default). false → the repair pass runs
   *  on the server's own findings alone, like the Fast engine. */
  piReviewPass?: boolean | null;
  /** May a Pi build DRAW — inline SVG diagrams, infographics, schematics —
   *  where a picture explains a how-to better than prose? The slide rules
   *  say not to (`slide-rules.md`, "Don't draw illustrations in SVG"),
   *  because the models that rule was written against drew crude shapes;
   *  this lifts that one rule for a model that draws well. absent/null/false
   *  → the rule stands (the default). true → every Pi build, review and
   *  repair spawn is told the rule is lifted and what still holds. */
  piSvgArt?: boolean | null;
  /** Does a Pi build get the LEAN wrapper — the short Claude-shaped prompt
   *  (follow the skill, everything is in intake.json) instead of the full
   *  defensive harness notes? The functional wiring survives either way
   *  (plan file, phase markers, photo route, skip-create); what the lean
   *  wrapper drops is the register — tool lectures, read-cap drills, the
   *  spelled-out allowlist. absent/null/true → LEAN (the default since
   *  2026-08-14: the 2x2 A/B measured lean as good or better on frontier
   *  AND small models, the loops having made the drill redundant). false →
   *  the full harness notes, the escape hatch for a model that measurably
   *  needs the drill. */
  piLeanPrompt?: boolean | null;
  /** The model that fresh-eyes review runs on, when it should not be the build
   *  one — on the SAME endpoint with the SAME key (a second model id, never a
   *  second account; the review-endpoint stack was removed with pi-lite,
   *  2026-08-08). Filled when the build model cannot see: the review hands the
   *  model pictures, so a text-only build model (cheap, writes well) pairs
   *  with a vision-capable reviewer here, e.g. mimo-v2.5 on the Go gateway.
   *  null/absent → the review runs on the build model, from source when that
   *  model is text-only. A model the endpoint refuses costs the review its
   *  first spawn and never the build — it re-runs on the build model. */
  piReviewModel?: string | null;
  /** When the model doing the fresh-eyes review is known to be TEXT-ONLY, does
   *  the server look at every render with the gateway's vision model first and
   *  hand the review its findings as text? absent/null/true → yes (the
   *  default). false → a blind reviewer reads the deck's source instead, which
   *  is the pre-2026-08 behaviour. Models the review roster knows can see, and
   *  ids it has never heard of, skip the pass either way — this only changes
   *  what a KNOWN-blind reviewer gets. Rides the gateway vision credential; a
   *  deploy without one degrades to the blind-source path with a transcript
   *  note, never an error. */
  piRenderDescribe?: boolean | null;
  /** Thinking rung for a Pi BUILD: off|minimal|low|medium|high|xhigh|max, Pi's
   *  own ladder. null/absent → env `PI_BUILD_EFFORT`, then nothing is passed at
   *  all and the model's own default stands. */
  piBuildEffort?: string | null;
  /** May a Pi build delegate its content sections to parallel child agents (the
   *  studio's vendored `subagent` extension)? absent/null/true → yes (the
   *  default); false → the parent writes every slide itself. Each child starts
   *  with an EMPTY context and is handed a self-contained brief, which is what
   *  `deck-building-claude` SKILL.md step 5 describes. */
  piSubagents?: boolean | null;
  /** The model a Pi CHAT turn runs on, when it should NOT be the build one.
   *  null/absent → `piModel`, then env `PI_MODEL`. Same reasoning as
   *  the Fast engine's own chat slot: editing one slide and writing a whole deck are different
   *  asks, so pinning a build to a deep model must not drag every message onto
   *  it. Blank keeps the two together, which is what a one-model server wants.
   *
   *  It is NOT a second endpoint or a second key — Pi has one of each, and a
   *  chat turn spends exactly what a build does. */
  piChatModel?: string | null;
  /** Thinking rung for a Pi CHAT turn, from the same ladder as the build dial
   *  (PI_EFFORT_LEVELS — it is the harness's ladder, not the model's).
   *  null/absent → env `PI_CHAT_EFFORT`, then the BUILD rung, then nothing is
   *  passed at all. Falling back to the build rung rather than to nothing is
   *  deliberate and matches `piChatModel`: this engine's dials are one
   *  provider's, and a server that set a rung once means it. */
  piChatEffort?: string | null;
  /** The context window the generated `models.json` declares for the picked
   *  model. REQUIRED by Pi rather than cosmetic: auto-compaction reserves
   *  against this number, so declaring one far larger than the endpoint serves
   *  produces a mid-build hard failure instead of a compaction. null/absent →
   *  128000, which every model this studio has met can carry. */
  piContextWindow?: number | null;
  /** Which engine the chat model pill STARTS on: 'claude' (default),
   *  'fast' or 'pi'. A preference, not a lock — the pill still switches per
   *  message, exactly as the wizard can override the default build engine for
   *  one build. An engine that isn't available on this server reduces back to
   *  'claude'. */
  chatEngine?: 'claude' | 'fast' | 'pi' | null;
  /** Reasoning effort for Claude CHAT turns (`claude -p --effort`) — the
   *  latency/quality dial. Chat only: builds are the product and keep the
   *  CLI default. null/absent → env CLAUDE_CHAT_EFFORT, then 'medium'. */
  claudeChatEffort?: string | null;
  /** Whether Sonnet is offered at all. absent/null/false → it isn't: the chat
   *  pill shows Opus alone and a 'sonnet' pick (a stale client, an old saved
   *  claudeModel) coerces to Opus. true → both are offered again. */
  offerSonnet?: boolean | null;
  /** Days a soft-deleted deck stays in decks/.trash before the sweep purges
   *  it. null/absent → DEFAULT_TRASH_RETENTION_DAYS (7). Clamped 1..365. */
  trashRetentionDays?: number | null;
  /** Whitespace floor — the minimum share (0..1) of the frame a slide's content
   *  should cover before the build REPORTS it as under-filled (the 2D area-fill
   *  measure, slide-metrics.js). 0 = off (no under-fill detection). Reporting
   *  only: nothing rewrites a slide because of this number — see the fill-loop
   *  retirement note in `agent/overflow-check.ts`. Default 0.4. */
  fillFloor?: number | null;
};

/** What the metered (Fast) layer currently resolves to, all fallbacks applied.
 *  Declared here for the same reason as `LLMSettings`; `llm/client.ts`'s
 *  `llmStatus()` returns it. */
export type LLMStatus = {
  provider: string | null;
  configured: boolean;
  reason?: string;
  model?: string;
  /** Effective reasoning effort. On the openai preset: settings, then env, then
   *  the 'high' default. On the gateway: settings, then env, or ABSENT when no
   *  rung is set — which is the gateway's normal state and means the parameter
   *  is never sent. */
  effort?: string;
  source?: 'env' | 'settings';
};

/** The three regenerable caches the ⚙ Storage section can clear. Declared here
 *  because `server/lib/disk-cache.ts`, which measures and clears them, imports
 *  `node:fs`; that module re-exports these. */
export type CacheKind = 'renders' | 'thumbs' | 'builds';

export type CacheUsage = {
  kind: CacheKind;
  label: string;
  /** What clearing it costs the user, in one sentence. */
  note: string;
  bytes: number;
  /** How many decks contribute — the drawer says "3 decks", not "17 files". */
  decks: number;
};

export type CacheCleared = { kind: CacheKind; bytes: number; decks: number };

/** `GET /api/settings/cache-usage`. A failed measure answers 500 with an empty
 *  list plus the reason, so the panel can say why rather than showing nothing. */
export type CacheUsagePayload = { caches: CacheUsage[]; error?: string };

/** `POST /api/settings/clear-cache` — `bytes` is the total actually freed. */
export type ClearCachePayload = { ok: true; cleared: CacheCleared[]; bytes: number };

/** A headless build as the ⚙ Builds panel lists it. The unions live here so
 *  the server's own `BuildJob` and this row can't drift apart. */
/** 'claude' is the whole-deck build on the Claude subscription
 *  (agent/claude-runner.ts); 'fast' is the same build on the ⚙ drawer's metered
 *  Fast model via the opencode CLI (agent/fast-runner.ts); 'pi' is the same
 *  build on the Pi
 *  CLI pointed at any OpenAI-compatible endpoint the ⚙ drawer names
 *  (agent/pi-runner.ts), always metered; 'section' is a job scoped to building
 *  ONE new section into an existing deck (agent/section-runner.ts), and says
 *  nothing about WHICH engine runs it — see SectionEngine. */
export type BuildEngine = 'claude' | 'fast' | 'pi' | 'section';

/** Which of the real engines builds a section. Kept apart from
 *  `BuildEngine` because 'section' occupies that slot: the job registry and the
 *  plan/resume routes key off `engine === 'section'` to recognise a section job,
 *  so the author's engine choice rides alongside on `sectionEngine` instead. The
 *  same union is the wizard's Build engine row minus 'section' itself. */
export type SectionEngine = Exclude<BuildEngine, 'section'>;
export type BuildStatus = 'running' | 'paused' | 'done' | 'failed' | 'cancelled';
export type BuildStage = 'planning' | 'building';

export type BuildJobRow = {
  id: string;
  slug: string;
  engine: BuildEngine;
  status: BuildStatus;
  stage: BuildStage;
  startedAt: number;
  /** Latest phase label seen in the event history, if any. */
  phase?: string;
  /** Slides written so far (latest slide-count event). */
  slides?: number;
  /** Section builds only: the section being written. The Library reads it to
   *  know which shelf item is still filling — a client-side flag could not,
   *  because it does not survive a reload or a walk to another page, and the
   *  item's tile then went back to offering "Add the first slide" on a build
   *  that was still running. */
  sectionKey?: string;
  /** Why a failed job failed — the last `error` event in its history.
   *
   *  A build's own account of itself used to live only in its event stream,
   *  which nothing outside the build screen reads. A case-study import runs
   *  with no screen at all, so a failure was completely silent: a thin item on
   *  the shelf and no explanation anywhere a person would look. Carrying the
   *  reason on the row is what lets a surface that never attached to the stream
   *  still say what went wrong. */
  error?: string;
};

/** `GET /api/wizard/builds` — the jobs plus the slot budget, since a stuck job
 *  matters because of what it is holding. */
export type BuildsPayload = { builds: BuildJobRow[]; running: number; maxConcurrent: number };

/** `POST /api/wizard/blank` — the deterministic cover + thank-you deck. */
export type BlankDeckPayload = { slug: string };

/** `POST /api/wizard/build-headless` and `…/build-retry/:id`. Both answer the
 *  same pair, which is why a retry can replace the job wholesale. `busy`/
 *  `reason` ride on the ERROR body when no build slot is free. */
export type BuildStartPayload = { jobId: string; slug: string };

/** The phases a build reports, in order. Named so `PHASE_LABELS` is keyed by
 *  exactly this union — every lookup then resolves to a string rather than
 *  `string | undefined`, and adding a phase without a label is a type error. */
export type BuildPhase =
  /** Reading the upload — a PDF's text extraction and its page renders. This
   *  used to happen inside the HTTP request, which meant a big deck export blew
   *  the server's connection timeout before the wizard ever got a job id. It is
   *  a build phase now, so it has a name and shows progress like any other. */
  'ingesting' | 'starting' | 'planning' | 'scaffolding' | 'building' | 'reviewing' | 'checking' | 'repairing' | 'done';

/**
 * One frame of `GET /api/wizard/build-progress/:jobId`.
 *
 * Declared here rather than in `agent/build-jobs.ts` because BOTH client
 * surfaces read this stream — the new-deck wizard and the in-editor section
 * build — and each had hand-written its own union of what it thought arrives.
 * They had already drifted: the section hook's `phase` frame carried no
 * `label` and its `slide` frame no `count`, which are exactly the two fields
 * the wizard reads. The registry re-exports this, so no server importer moved.
 */
export type BuildEvent =
  | { type: 'slug'; slug: string }
  | { type: 'phase'; phase: BuildPhase; label: string }
  | { type: 'tool'; tool: string; title?: string; path?: string }
  | { type: 'slide'; count: number }
  | { type: 'text'; text: string }
  | { type: 'stderr'; text: string }
  // A reviewable plan card in the build UI (section builds pause on one).
  // `edited` marks a user-modified re-emit.
  | {
      type: 'plan';
      plan: { sections: PlanSection[]; slides: PlannedSlide[]; rationale?: string };
      edited?: boolean;
    }
  // Post-build conformance findings (warnings only, never failures).
  | { type: 'conformance'; warnings: string[] }
  // The build is waiting on the user (section plan review).
  | { type: 'paused' }
  | { type: 'error'; message: string; sessionId?: string }
  | { type: 'done'; code: number; slug: string };

/** `GET /api/settings/gateway-models` — the gateway's own OpenAI-shape
 *  `/models`, proxied. Degrades to an empty list plus a reason, which the
 *  drawer renders as a free-text field rather than an error. */
export type GatewayModelsPayload = {
  models: string[];
  /** Model id → the rungs THAT model takes. Absent for a roster we could not
   *  read, which is why the drawer falls back to the union rather than
   *  blocking the author on our ignorance. */
  efforts?: Record<string, string[] | undefined>;
  /** Model id → whether that model can READ an image. Drives the picker's
   *  `· reads images` / `· text only` annotations and the brief-image roster.
   *  Absent for a roster we could not read. */
  vision?: Record<string, boolean | undefined>;
  error?: string;
};

/** `GET /api/settings/claude-resolved-model` — what the installed CLI maps each
 *  alias to. Per-alias rather than whole-response failure: one probe can fail
 *  while the other answers. */
export type ClaudeResolvedModels = {
  sonnet?: { model?: string; error?: string } | null;
  opus?: { model?: string; error?: string } | null;
};

declare global {
  interface WindowEventMap {
    /** Fired by the ⚙ drawer on save, carrying the fresh `GET /api/settings`
     *  body — that is why `useModelChoice` is a subscription rather than a
     *  fetch per component. */
    'studio:settings-changed': CustomEvent<SettingsPayload>;
    /** "Refetch the activity feed" — dispatched after an apply/reject/revert.
     *  Carries nothing; every listener re-reads the endpoint. */
    'studio:activity-refresh': Event;
  }
}

/** `GET /api/decks` — the dashboard's whole world in one request. */
export type DecksPayload = {
  decks: DeckSummary[];
  /** The category registry's display order, including deliberately-empty
   *  categories the sidebar still shows.
   *
   *  Kept alongside the richer `categories` below rather than replaced by it:
   *  Library, BankLibrary and the assets modal all read this shape, and none of
   *  them has a use for a colour. */
  categoryOrder: string[];
  /** The same categories with their colour, sidebar visibility and which one
   *  new decks default to. */
  categories: CategoryEntry[];
  /** Every registered tag, in sidebar order, with its colour. A deck may also
   *  carry a tag absent from here; the sidebar merges those in alphabetically
   *  after the arranged ones. */
  tags: TagEntry[];
  /** Everybody a deck can be attributed to. */
  people: Person[];
  /** Named filters, each holding the dashboard's URL query string. */
  views: SavedView[];
  slugPrefix: string;
};

/** `GET /api/people`, and the body every mutation there answers with, so the
 *  client never has to refetch to redraw the list. */
export type PeoplePayload = { people: Person[] };
/** `GET /api/tags`, and the body every mutation there answers with. */
export type TagsPayload = { tags: TagEntry[] };
/** The body every /api/categories mutation that changes metadata answers with. */
export type CategoriesPayload = { categories: CategoryEntry[] };
/** `GET /api/views`, and the body every mutation there answers with. */
export type ViewsPayload = { views: SavedView[] };

/**
 * `POST /api/decks/bulk` — one organisation change across many decks.
 *
 * Partial success is the contract, not an error case: a deck a build is
 * currently writing lands in `failed` while the rest go through, so the toast
 * reads "24 moved · 1 skipped (building)". `previous` carries what each deck
 * held before, so the undo is one more call with `op: 'restore'`.
 */
export type BulkDecksOk = {
  ok: true;
  applied: {
    slug: string;
    previous: { categories?: string[]; tags?: string[]; ownerId?: string | null; starred?: boolean };
  }[];
  failed: { slug: string; error: string }[];
};
export type ViewOk = { ok: true; view: SavedView; views: SavedView[] };
/** A tag rename/delete — `affected` is how many decks were rewritten. */
export type TagCascadeOk = { ok: true; affected: number; tags: TagEntry[] };
export type PersonOk = { ok: true; person: Person; people: Person[] };
/** `DELETE /api/people/:id` — `affected` is how many decks were re-attributed. */
export type PersonDeleteOk = { ok: true; affected: number; people: Person[] };

/**
 * `PUT /api/decks/:slug/rename`, on success. Declared here rather than in
 * `lib/deck-rename.ts` (which imports `node:fs` and the Vite pool) because the
 * dashboard has to TELL the user what a rename did: a slug change moves the
 * directory and takes a published deck offline, and `unpublished` is the only
 * thing that says so. That module re-exports it.
 */
export type RenameOk = {
  ok: true;
  slug: string;
  oldSlug: string;
  title: string;
  /** False for a title-only change — the link and every URL are unaffected. */
  slugChanged: boolean;
  /** True when the rename took a published deck offline. */
  unpublished: boolean;
};

/** `POST /api/decks/:slug/duplicate`. `newDir` is the server's own path; the
 *  client navigates by `newSlug`. */
export type DeckDuplicatePayload = { ok: true; newDir: string; newSlug: string; newTitle: string };

/** `GET /api/decks/:slug/name-info` — what the rename/duplicate dialogs need
 *  BEFORE they open, so the client never re-derives a default it would then
 *  have to keep in step with the server. */
export type DeckNameInfoPayload = {
  slug: string;
  title: string;
  duplicateName: string;
  duplicateSlug: string;
  slugPrefix: string;
};

/** `POST /api/decks/import`. */
export type DeckImportPayload = { ok: true; slug: string };

/**
 * A soft-deleted deck. Declared here rather than in `codecs/deck-codec.ts`
 * (which imports `node:fs`) because the dashboard's Trash panel RENDERS these
 * rows — the id it posts back to restore/purge, and the `deletedAt` it counts
 * the retention window from. That module re-exports it, so no server importer
 * moved.
 */
export type TrashEntry = {
  id: string;
  slug: string;
  title: string;
  deletedAt: number;
  /** What was deleted, stamped before the directory moved (see DeckSummary.kind).
   *  Absent on entries trashed before this field existed. Without it the Trash
   *  panel would present a library item as an ordinary presentation. */
  kind?: 'bank' | 'library-item' | null;
};

/** `GET /api/decks/trash`. `retentionDays` is the setting, not a per-entry
 *  value: the panel edits it inline for all of them at once. */
export type TrashPayload = { trash: TrashEntry[]; retentionDays: number };

/** One brand-rule problem found by the deck-wide scan. `idx` is the slide's
 *  position, so the drawer can number it without a second lookup. */
export type ValidationIssue = {
  slide: string;
  idx: number;
  field: string;
  severity: 'error' | 'warn';
  message: string;
};

/** `GET /api/build/:slug/validation` — every field of every slide, scanned. */
export type ValidationPayload = {
  issues: ValidationIssue[];
  /** The publish gate: non-zero blocks the Publish button. */
  errorCount: number;
};

/** The last PDF render's outcome, as `lib/pdf-log.ts` persists it. Lives here
 *  rather than beside the writer because that module imports `node:fs`, so the
 *  drawer that reads this over the wire could never see it. */
export type PdfStatus = {
  slug: string;
  state: 'running' | 'ok' | 'failed';
  exitCode: number | null;
  startedAt: string;
  finishedAt: string | null;
  source: 'publish' | 'build-pdf';
};

/** `GET /api/build/:slug/pdf-log` — why the public PDF link 404s, when it
 *  does. `status` is null before the deck's first render; `exists` is the
 *  question the drawer actually asks, since exit 0 is not success. */
export type PdfLogPayload = {
  status: PdfStatus | null;
  log: string;
  exists: boolean;
};

/** `PUT /api/decks/:slug/view-password` and `…/display-date` — each echoes the
 *  value it settled on, which is not always the one sent (blank and whitespace
 *  both mean "clear it"). */
export type ViewPasswordPayload = { ok: true; slug: string; viewPassword: string | null };
export type DisplayDatePayload = { ok: true; slug: string; displayDate: string | null };

/** `GET /api/decks/:slug/thumbs` — cached thumbnails plus the layout findings
 *  that rode along with each render. */
export type DeckThumbsPayload = {
  thumbs: Record<string, { url: string; hash: string }>;
  pending: string[];
  /** Epoch ms until which generation for this deck is backed off, or null when
   *  it isn't. Set when a pass captured NOTHING — i.e. every pending slide is
   *  failing the same way. Clients poll `pending` every 800ms, and without this
   *  they cannot tell "still rendering" from "the server has given up": pending
   *  stays non-empty either way, so the poll runs forever and whatever it holds
   *  (a spinner, a generation lock) is never released. Treat a future value as
   *  terminal for this attempt. */
  coolingUntil: number | null;
  /** True when a pass this call did not start owns the slug, so nothing was
   *  queued and `pending` will not move until that pass frees the deck. */
  busy: boolean;
  /** Why a call was told `busy`, so a client queue can tell a productive wait
   *  from an unproductive one:
   *   - `'deck'` — THIS deck's own whole-deck pass holds the slot and is
   *     rendering the exact slides we want. Waiting is productive; keep the
   *     deck queued (that pass has its own deadline and is swept if wedged).
   *   - `'capacity'` — the global pass slot is held by a DIFFERENT deck, so
   *     no image we want is being made. Unproductive; a client should cap this
   *     wait and surface the tiles as retryable, or a grid starves forever
   *     behind one slow deck (the dashboard's cover queue vs an open overview).
   *  Absent whenever `busy` is false. */
  busyKind?: 'deck' | 'capacity';
  /** The slide ids THIS call queued for rendering. A viewport client needs it to
   *  know what will actually arrive: an id it asked for that is neither already
   *  in `thumbs` nor here is one nothing is going to render — it is cached
   *  already, it has been given up on, or it is past `MAX_SLIDES` and not in the
   *  deck's plan at all. Without this the client cannot tell those from "queued"
   *  and waits for an image that is never coming. Empty on a read-only call. */
  accepted: string[];
  /** Slides the server has stopped trying to render (SLIDE_FAIL_LIMIT strikes on
   *  the current content hash). This is the honest source of a tile's "failed"
   *  state — server-side, so every surface reports it identically — and it clears
   *  itself when the slide is edited, because the key includes the hash. */
  givenUp: string[];
  /** Slides beyond MAX_SLIDES, which this pipeline will never render. 0 for
   *  almost every deck. Reported because the cap used to be INVISIBLE: those
   *  slides appeared in neither `thumbs` nor `pending`, so the server said
   *  "done" while clients — counting against their own uncapped slide list —
   *  showed ghosts that nothing could ever fill. The one deck that ever hit it
   *  was the old library shelf, which grew without bound; now that each item
   *  owns a deck, nothing in the library approaches the cap. */
  truncated: number;
  /** Slide id → the layout problems found the last time it was rendered, for
   *  slides that have been measured. A slide absent from this map has not been
   *  measured yet (it is listed in `pending` until it has been); a slide mapped
   *  to `[]` was measured and is clean. See pipeline/slide-findings.ts. */
  findings: Record<string, LayoutFinding[]>;
};

/** One slide of a library item, as `GET /api/library` lists it. */
export type LibrarySlide = {
  id: string;
  /** Position in the ITEM's own deck. Since an item owns its deck, this is
   *  both a real preview index and a within-item one — the two stopped being
   *  different things when the shared shelf went away. */
  idx: number;
  title: string;
  section: string;
  sectionName: string;
};

/**
 * One library ITEM: a self-contained slide or series, owning its own deck
 * (server: pipeline/library-items.ts).
 *
 * `count` can be 0 — a just-created item exists before its first slide, and an
 * import files one before its build starts; both stay listed, and consumers
 * that INSERT filter them out.
 */
export type LibraryItem = {
  /** The item's deck slug, `lib-…`. The identifier everywhere. */
  slug: string;
  name: string;
  color?: string;
  count: number;
  /** A browsing label (free text), not the deck category system. */
  category: string | null;
  sections: { key: string; name: string; color: string; count: number }[];
  slides: LibrarySlide[];
  /** A people.json id, inherited from the deck the item was saved out of.
   *  Null for an item created empty here, and for everything saved before the
   *  inheritance existed; those render with a date and no name. */
  ownerId: string | null;
  /** Last edit of the item's own deck, ms epoch. The same number a deck card
   *  shows, because an item IS a deck. */
  savedAt: number;
  /** How many times this item has been inserted into a deck, ever. 0 for an
   *  item nobody has used yet, and for every item on a studio whose usage
   *  registry was written after the fact (server/codecs/library-usage-registry). */
  inserts: number;
  /** Epoch ms of the most recent insert. Absent while `inserts` is 0. */
  lastInsertedAt?: number;
  /** How many DISTINCT decks hold a copy — "used in 6 decks". Always at most
   *  `inserts`: re-inserting into the same deck does not raise it. */
  usedInDecks: number;
  /** Cached cover thumbnail, or null when none has been rendered yet. A pure
   *  cache read: a listing must never spawn N deck Vite children, so the grid
   *  asks for what it can see, one deck at a time. */
  cover?: string | null;
};

/** `GET /api/library`. */
export type LibraryPayload = {
  items: LibraryItem[];
  /** The shelf registry with each shelf's optional colour — the rail's folder
   *  dots and the grouped body's heading dots. Same entries as `categoryOrder`. */
  categories: CategoryEntry[];
  /** The people registry, so a card can turn its `ownerId` into a name without
   *  a second round trip. Same list `GET /api/decks` carries, same reason. */
  people: Person[];
  /** The registry, INCLUDING empty shelves — what the sidebar tree is built
   *  from, so a shelf with nothing on it still has a home. */
  categoryOrder: string[];
};

/** `GET /api/bank`. */
export type BankPayload = {
  items: BankItem[];
  categories: BankCategory[];
  categoryOrder?: string[];
};

/**
 * `POST /api/library/save` — slides copied into a library shelf as one item.
 * The undo path is what reads most of this: whether the save CREATED the item
 * (undo removes the whole thing) or appended to one (undo removes only the
 * slides it added).
 */
export type LibrarySavePayload = {
  ok: true;
  /** The ITEM's deck slug — the undo acts on this deck, not `fromSlug`. */
  slug: string;
  /** Kept for the single-slide callers that predate `slideIds`. */
  slideId?: string;
  slideIds: string[];
  /** The first saved slide's title, for the toast. */
  title: string;
  /** The item's display name. */
  name: string;
  /** False when the slides were appended to an item that already existed,
   *  which is what decides whether undo removes the item or just its slides. */
  createdItem: boolean;
  sectionKeys: string[];
  count: number;
  /** Slides the save refused (section dividers), when any. */
  skipped?: string[];
};

/**
 * One chat skill on `GET /api/skills` — the command bar's "/" autocomplete.
 * The skill's BODY is deliberately absent: it is a system block the server
 * injects, never something the client holds.
 */
export type SkillSummary = {
  name: string;
  description: string;
  /** Which chat scope offers it. A selection turn rides the deck endpoint, so
   *  it takes deck skills. */
  scope: 'slide' | 'deck';
};

/** `GET /api/skills`. */
export type SkillsPayload = { skills: SkillSummary[] };

/**
 * One ready-made slide on `GET /api/templates` — a projection of the server's
 * `TemplateEntry`, not the whole thing (that lives in `llm/templates.ts`,
 * which scans the filesystem). Declared here because the picker's text-list
 * fallback renders these directly and then hands one back as a pick.
 */
export type TemplateSummary = {
  componentName: string;
  title: string;
  description: string;
  props: string[];
  importPath: string;
  /** 'closing/thank-you' — path under template/slides/, no extension. */
  pathTail: string;
  category: string;
  tags: string[];
  /** Variant NAMES only; the full variant objects stay server-side. */
  variants: string[];
  workbenchHash: string;
};

/** `GET /api/templates`. */
export type TemplatesPayload = { templates: TemplateSummary[] };

/** One file in a deck's `assets/` directory. */
export type DeckAsset = {
  name: string;
  size: number;
  /** Epoch ms; the listing is sorted newest-first on it. */
  mtime: number;
};

/** `GET /api/assets/:slug`. */
export type DeckAssetsPayload = { assets: DeckAsset[] };

/**
 * One card `POST /api/chat/:slug/_deck/apply-all` could not apply. `tool` is
 * what the transcript's failure sentence names, and `error` is the server's
 * own message shown verbatim beside it.
 */
export type DeckApplyFailure = {
  tool_id?: string;
  tool: string;
  error: string;
  details?: unknown;
};

/**
 * `POST /api/chat/:slug/_deck/apply-all`. Best-effort by design: `applied` and
 * `failed` partition the batch and `ok` is only `failed.length === 0`, so a
 * caller that branches on `ok` alone learns nothing about which card broke.
 * Each applied entry also carries whatever that tool returned.
 */
export type DeckApplyAllPayload = {
  ok: boolean;
  applied: ({ tool_id?: string; tool: string } & Record<string, unknown>)[];
  failed: DeckApplyFailure[];
};

/** `POST /api/chat/:slug/_deck/reject-all` — metadata only, so nothing can
 *  partially fail. `rejected` lists the tool ids marked, not a count. */
export type DeckRejectAllPayload = { ok: true; rejected: string[] };

/**
 * `POST /api/bank/team-sync`, both the `{dryRun: true}` preview and the real
 * run. Declared here rather than in `pipeline/team-sync.ts` (which imports
 * `node:path` and the `node:fs`-backed asset bank) so the confirm dialog that
 * RENDERS the diff reads the same shape the sync produced; that module
 * re-exports it, so no server importer moved.
 */
/** One slide printing a renamed person, with the prop edits that would bring
 *  it up to date. `edits` is advisory: the server re-derives it inside the deck
 *  lock before writing, so a slide edited since the scan is handled, not
 *  clobbered. */
export type PersonNameSlideHit = {
  id: string;
  idx: number;
  title: string;
  edits: { id: string; value: string }[];
};

/** `GET /api/bank/items/:id/usage`. `complete: false` means at least one deck
 *  could not be read, so the list is a floor and not an inventory — say so
 *  rather than implying the rest are clean. */
export type PersonNameUsage = {
  from: string;
  to: string;
  decks: { slug: string; slides: PersonNameSlideHit[] }[];
  complete: boolean;
};

/** `POST /api/bank/items/:id/rename-in-decks`. Per-slide best effort: whatever
 *  could not be rewritten is named, never silently dropped. */
export type PersonRenameApplied = {
  updated: { slug: string; slideId: string; fields: number }[];
  skipped: { slug: string; slideId?: string; reason: string }[];
};

/** One bank person the site no longer lists, and who on the site they might
 *  have become. Grouped per person rather than offered as every bank × site
 *  pair: three departures against forty new names is a hundred-odd pairs and
 *  three questions. Options are ordered most-likely first (a surname change
 *  usually keeps the first name). */
export type TeamRenameCandidate = {
  bank: { id: string; name: string };
  options: { id: string; name: string }[];
};

export type TeamSyncResult = {
  dryRun: boolean;
  added: { id: string; name: string }[];
  updated: { id: string; name: string }[];
  /** Matched people whose display name moved. Their id, portrait and every
   *  deck already built are untouched; the outgoing name becomes a former one. */
  renamed: { id: string; from: string; to: string }[];
  /** People genuinely gone from the team page. */
  removed: { id: string; name: string }[];
  /** Bank items whose name still matches someone on the site, but under a
   *  different id (a manual upload landing next to the synced entry gets a
   *  "-2" suffix). The synced entry is kept; these are dupes, not leavers. */
  removedDuplicates: { id: string; name: string }[];
  /** Unmatched on both sides, so possibly one person under a new name. The
   *  dry run offers these; only a pairing the user picks is ever acted on, and
   *  until then the pair is still reported as an add plus a remove. */
  renameCandidates: TeamRenameCandidate[];
  unchanged: number;
  /** Per-person problems (portrait download failed …) — never fatal. */
  errors: string[];
};

/** One published case study, as the wizard's picker lists it. Declared here
 *  because `pipeline/case-studies.ts` imports `node:fs` and the picker cannot.
 *  `slug` is the site's own URL segment and the import's only input. */
export type CaseStudyCard = {
  slug: string;
  /** The client, from the card's own heading ("Loughborough Schools Foundation"). */
  client: string;
  /** The project title ("Protecting children's identities for …"). */
  title: string;
  /** Absolute URL of the square thumbnail, or null if the card had no picture. */
  thumb: string | null;
};

export type CaseStudyIndexResponse = {
  cards: CaseStudyCard[];
  /** ISO timestamp of the last successful sync; null before the first one. */
  syncedAt: string | null;
  /** The picker refreshes in the background when this is true. */
  stale: boolean;
};

/** One picture's description as it travels to the build. Mirrors the wizard's
 *  own `SerialisedImageNote`; a case-study import writes these itself, because
 *  the author never sees these files in a picker and the filename is all the
 *  build agent would otherwise have. */
export type ImageNoteWire = {
  file: string;
  caption?: string;
  section?: string;
  weight?: 'hero' | 'optional';
  use?: 'reference';
};

/** What the wizard gets back when it imports a case study: enough to show what
 *  was found, plus the upload handle the build will send instead of files. */
export type CaseStudyImportResponse = {
  /** Handle on the bytes the server kept (`pipeline/upload-store.ts`). */
  uploadId: string;
  /** The stored document's name, which becomes the brief's filename. */
  name: string;
  slug: string;
  title: string;
  url: string;
  markdown: string;
  headings: string[];
  imageNotes: ImageNoteWire[];
  /** Counts, for the "4 images, 3 videos" line the picker shows. */
  images: number;
  videos: number;
  /** Filenames in the upload's media directory, in page order. */
  media: string[];
  /** The same shape a dropped document's preview returns, so every derivation
   *  the wizard already has (title, slide estimate, "belongs with" headings)
   *  works on an imported case study without a second code path. */
  summary: IngestSummary;
  warnings: string[];
};

/**
 * One file the new-deck wizard's ingest preview read. Declared here rather
 * than in `pipeline/ingest.ts` (which imports `node:fs`) so the wizard's own
 * derivations — title, slide estimate, fidelity recommendation — read the
 * same shape the server produced.
 */
export type IngestedFile = {
  name: string;
  type: 'text' | 'pdf' | 'image' | 'deck';
  size: number;
  /** Extracted text content (text/pdf/deck), undefined for images. */
  text?: string;
  /** Truncated outline summary (first 80 lines), used in plan prompts. */
  outline?: string;
  /** The brief's own section headings, read from the WHOLE text (not the
   *  truncated outline). The wizard's "describe your images" step offers these
   *  as the "belongs with" choices — they are the author's own words, and they
   *  exist before the deck does, which slide titles do not. */
  headings?: string[];
  /** Present when the upload is a presentation to rebuild (pptx, or a PDF
   *  that reads as a slide export) — drives the wizard's rebuild banner. */
  deck?: { kind: 'pptx' | 'pdf'; slideCount: number };
  /** Handle on the bytes the preview kept server-side, so the build that
   *  follows can reference this file instead of uploading it a second time
   *  (`pipeline/upload-store.ts`). Absent when the store refused the write —
   *  the client then falls back to sending the file, which is what it always
   *  did. */
  uploadId?: string;
  warnings?: string[];
  /** Browser-only: set when the file arrived inside a dropped FOLDER, which is
   *  how a sidecar (image credits, licences) is told from the real brief. */
  webkitRelativePath?: string;
};

export type IngestSummary = {
  files: IngestedFile[];
  /** Characters, not words — the /5 that turns it into words lives at each
   *  reader, and there are three of them. */
  totalText: number;
  estSlides: { live: number; readout: number };
};

/** The error body every route returns on a non-2xx. `code` is present only
 *  where a caller is expected to branch on it — `client/lib/useSlideFields.js`
 *  reads `stale` vs `preview` off it rather than string-matching the message,
 *  so any new 409 needs one too (see studio/CLAUDE.md). */
export type ApiErrorBody = {
  error: string;
  code?: 'stale' | 'preview' | 'staging-guard';
  /** 'staging-guard' only: where the real studio is, so the client can offer
   *  the way out without knowing the address itself. */
  liveUrl?: string;
};

/** `GET /api/env` — what the client needs before it renders anything, and the
 *  only endpoint besides healthz and login that answers without a session (the
 *  banner has to appear on the login page too). Deliberately holds nothing but
 *  this: it is the one payload an unauthenticated stranger can read. */
export type EnvPayload = {
  /** True on the staging copy. Drives the banner and the spend dialog. */
  staging: boolean;
  /** Where real decks are made. Present whether or not `staging` is set, so
   *  the client never has to carry a hardcoded address. */
  liveUrl: string;
  /** The Google OAuth client id, when this server offers Google sign-in.
   *  Absent means the button does not render — which is the whole of the
   *  client's decision, because the gates are the server's. */
  googleClientId?: string;
};

/** `GET /api/me` — the session, and who it belongs to.
 *
 *  Every session names somebody, so `person` is null only when that id no
 *  longer resolves: the record was deleted while the cookie was still valid. */
export type MePayload = {
  iat: number;
  exp: number;
  person: Person | null;
  /** Whether they may change who has access (`STUDIO_ADMINS` on the deploy).
   *  The client uses it to render, never to decide: the routes refuse
   *  independently, because a hidden button is not a permission. */
  isAdmin: boolean;
};
